// src/components/FileMergeSplit/SplitOptions.tsx
import React, { useState, useEffect } from 'react';
import { HelpCircle, Plus, X } from 'lucide-react';
import { formatFileSize } from '../../utils/formatUtils';

interface Range {
    start: number;
    end: number;
}

export interface SplitOptionsData {
    splitMethod?: 'pageRange' | 'extraction' | 'size' | 'chunks' | 'bookmarks';
    pageRangeMode?: 'fixed' | 'custom';
    pagesPerFile?: number;
    customRanges?: string;
    extractionPages?: string;
    remainingAction?: 'ignore' | 'bundle' | 'separate';
    maxSizeKB?: number;
    sizeUnit?: 'KB' | 'MB';
    chunks?: number;
    [key: string]: any;
}

interface SplitOptionsProps {
    file: {
        name: string;
        size: number;
        type: string;
        [key: string]: any;
    };
    options: SplitOptionsData;
    onChange: (options: SplitOptionsData) => void;
    onProcess: () => void;
    onBack: () => void;
    loading: boolean;
}

const SplitOptions: React.FC<SplitOptionsProps> = ({
    file,
    options,
    onChange,
    onProcess,
    onBack,
    loading
}) => {
    const [showTooltip, setShowTooltip] = useState<string | null>(null);
    const [customRanges, setCustomRanges] = useState<Range[]>([{ start: 1, end: 1 }]);
    const [customExtractions, setCustomExtractions] = useState<Range[]>([{ start: 1, end: 1 }]);

    // Make sure PDF-specific options are not selected for non-PDF files
    useEffect(() => {
        if (!file.type.includes('pdf') && (
            options.splitMethod === 'bookmarks' ||
            (options.splitMethod === 'pageRange' && options.pageRangeMode === 'custom')
        )) {
            // Set a more compatible method for non-PDF files
            handleOptionChange('splitMethod', 'chunks');
        }
    }, [file.type, options.splitMethod, options.pageRangeMode]);

    const handleOptionChange = (name: string, value: any) => {
        onChange({
            ...options,
            [name]: value
        });
    };

    // Handle custom ranges for the custom page range mode
    const updateCustomRanges = (ranges: Range[]) => {
        // Convert the array of range objects to a string format: "1-3,4-7,8-10"
        const rangesStr = ranges
            .map(range => range.start === range.end ? `${range.start}` : `${range.start}-${range.end}`)
            .join(',');

        handleOptionChange('customRanges', rangesStr);
    };

    // Add a new custom range
    const addCustomRange = () => {
        const lastRange = customRanges[customRanges.length - 1];
        const newStart = lastRange.end + 1;
        const newRanges = [...customRanges, { start: newStart, end: newStart }];
        setCustomRanges(newRanges);
        updateCustomRanges(newRanges);
    };

    // Update a specific custom range
    const updateRange = (index: number, field: 'start' | 'end', value: string) => {
        const newRanges = [...customRanges];
        newRanges[index][field] = parseInt(value) || 1;

        // Ensure end is never less than start
        if (field === 'start' && newRanges[index].end < newRanges[index].start) {
            newRanges[index].end = newRanges[index].start;
        }

        setCustomRanges(newRanges);
        updateCustomRanges(newRanges);
    };

    // Remove a custom range
    const removeCustomRange = (index: number) => {
        if (customRanges.length > 1) {
            const newRanges = customRanges.filter((_, i) => i !== index);
            setCustomRanges(newRanges);
            updateCustomRanges(newRanges);
        }
    };

    // Handle custom extractions for the extraction mode
    const updateCustomExtractions = (extractions: Range[]) => {
        // Convert the array of extraction objects to a string format: "3,5,9-10"
        const extractionsStr = extractions
            .map(range => range.start === range.end ? `${range.start}` : `${range.start}-${range.end}`)
            .join(',');

        handleOptionChange('extractionPages', extractionsStr);
    };

    // Add a new extraction range
    const addCustomExtraction = () => {
        const lastRange = customExtractions[customExtractions.length - 1];
        const newStart = lastRange.end + 1;
        const newExtractions = [...customExtractions, { start: newStart, end: newStart }];
        setCustomExtractions(newExtractions);
        updateCustomExtractions(newExtractions);
    };

    // Update a specific extraction range
    const updateExtraction = (index: number, field: 'start' | 'end', value: string) => {
        const newExtractions = [...customExtractions];
        newExtractions[index][field] = parseInt(value) || 1;

        // Ensure end is never less than start
        if (field === 'start' && newExtractions[index].end < newExtractions[index].start) {
            newExtractions[index].end = newExtractions[index].start;
        }

        setCustomExtractions(newExtractions);
        updateCustomExtractions(newExtractions);
    };

    // Remove an extraction range
    const removeCustomExtraction = (index: number) => {
        if (customExtractions.length > 1) {
            const newExtractions = customExtractions.filter((_, i) => i !== index);
            setCustomExtractions(newExtractions);
            updateCustomExtractions(newExtractions);
        }
    };

    // Parse existing custom ranges when component loads or when they change externally
    useEffect(() => {
        if (options.customRanges && options.splitMethod === 'pageRange' && options.pageRangeMode === 'custom') {
            const parts = options.customRanges.split(',');
            const parsedRanges = parts.map(part => {
                if (part.includes('-')) {
                    const [start, end] = part.split('-').map(num => parseInt(num) || 1);
                    return { start, end: Math.max(start, end) };
                } else {
                    const page = parseInt(part) || 1;
                    return { start: page, end: page };
                }
            });

            // Only update if the parsed ranges are different from current state
            if (JSON.stringify(parsedRanges) !== JSON.stringify(customRanges)) {
                setCustomRanges(parsedRanges.length > 0 ? parsedRanges : [{ start: 1, end: 1 }]);
            }
        }
    }, [options.customRanges, options.splitMethod, options.pageRangeMode]);

    // Parse existing extractions when component loads or when they change externally
    useEffect(() => {
        if (options.extractionPages && options.splitMethod === 'extraction') {
            const parts = options.extractionPages.split(',');
            const parsedExtractions = parts.map(part => {
                if (part.includes('-')) {
                    const [start, end] = part.split('-').map(num => parseInt(num) || 1);
                    return { start, end: Math.max(start, end) };
                } else {
                    const page = parseInt(part) || 1;
                    return { start: page, end: page };
                }
            });

            // Only update if the parsed extractions are different from current state
            if (JSON.stringify(parsedExtractions) !== JSON.stringify(customExtractions)) {
                setCustomExtractions(parsedExtractions.length > 0 ? parsedExtractions : [{ start: 1, end: 1 }]);
            }
        }
    }, [options.extractionPages, options.splitMethod]);

    return (
        <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden my-5 mx-auto max-w-[800px] dark:bg-slate-800 dark:border-slate-700">
            <div className="p-6 border-b border-slate-200 bg-white mb-5 shadow-[0_2px_4px_rgba(0,0,0,0.05)] rounded-lg dark:bg-slate-900 dark:border-slate-700 max-md:p-4">
                <h3 className="text-lg font-semibold text-slate-800 mt-0 mb-4 dark:text-slate-200">File Details</h3>
                <div className="flex flex-col gap-3">
                    <div className="flex flex-row items-baseline flex-wrap m-0">
                        <span className="font-semibold text-slate-600 mr-1.5 shrink-0 dark:text-slate-400">Name:</span>
                        <span className="text-slate-800 overflow-hidden text-ellipsis max-w-full break-words flex-1 dark:text-slate-200" title={file.name}>{file.name}</span>
                    </div>
                    <div className="flex flex-row items-baseline flex-wrap m-0">
                        <span className="font-semibold text-slate-600 mr-1.5 shrink-0 dark:text-slate-400">Size:</span>
                        <span className="text-slate-800 overflow-hidden text-ellipsis max-w-full break-words flex-1 dark:text-slate-200">{formatFileSize(file.size)}</span>
                    </div>
                </div>
            </div>

            <div className="p-6 border-b border-slate-200 bg-white mb-5 shadow-[0_2px_4px_rgba(0,0,0,0.05)] rounded-lg dark:bg-slate-900 dark:border-slate-700 max-md:p-4">
                <h3 className="text-lg font-semibold text-slate-800 mt-0 mb-4 dark:text-slate-200">Split Settings</h3>

                <div className="mb-6 last:mb-0">
                    <label className="flex items-center font-semibold text-slate-800 mb-1 dark:text-slate-200">Split Method</label>
                    <p className="text-sm text-slate-500 mt-0 mb-2 dark:text-slate-400">Choose how you want to split the file</p>
                    <select
                        value={options.splitMethod || 'pageRange'}
                        onChange={(e) => handleOptionChange('splitMethod', e.target.value)}
                        className="w-full p-2.5 bg-white border border-slate-300 rounded-md text-sm outline-none focus:border-primary appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg_xmlns=%27http://www.w3.org/2000/svg%27_fill=%27none%27_viewBox=%270_0_24_24%27_stroke=%27%2394a3b8%27%3E%3Cpath_stroke-linecap=%27round%27_stroke-linejoin=%27round%27_stroke-width=%272%27_d=%27M19_9l-7_7-7-7%27/%3E%3C/svg%3E')] bg-no-repeat bg-[position:right_10px_center] bg-[length:16px] dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                        disabled={loading}
                    >
                        <option value="pageRange">By Page Range</option>
                        <option value="extraction">Custom Extraction</option>
                        <option value="size">By File Size</option>
                        <option value="chunks">By Number of Parts</option>
                        {file.type.includes('pdf') && (
                            <option value="bookmarks">By Bookmarks</option>
                        )}
                    </select>
                </div>

                {options.splitMethod === 'pageRange' && (
                    <>
                        <div className="mb-6 last:mb-0">
                            <label className="flex items-center font-semibold text-slate-800 mb-1 relative dark:text-slate-200">
                                Page Range Mode
                                <span className="ml-1.5 cursor-help text-slate-500 inline-flex hover:text-primary" onMouseEnter={() => setShowTooltip('pageRangeMode')} onMouseLeave={() => setShowTooltip(null)}>
                                    <HelpCircle size={16} />
                                </span>
                            </label>
                            {showTooltip === 'pageRangeMode' && (
                                <div className="absolute bg-slate-800 text-white px-3 py-2 rounded text-[13px] z-[100] max-w-[300px] shadow-[0_2px_10px_rgba(0,0,0,0.2)] mt-1">
                                    <p className="m-1 m-t-0"><strong>Fixed:</strong> Split into parts with equal number of pages</p>
                                    <p className="m-1 m-b-0"><strong>Custom:</strong> Define your own page ranges</p>
                                </div>
                            )}
                            <select
                                value={options.pageRangeMode || 'fixed'}
                                onChange={(e) => handleOptionChange('pageRangeMode', e.target.value)}
                                className="w-full p-2.5 bg-white border border-slate-300 rounded-md text-sm outline-none focus:border-primary appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg_xmlns=%27http://www.w3.org/2000/svg%27_fill=%27none%27_viewBox=%270_0_24_24%27_stroke=%27%2394a3b8%27%3E%3Cpath_stroke-linecap=%27round%27_stroke-linejoin=%27round%27_stroke-width=%272%27_d=%27M19_9l-7_7-7-7%27/%3E%3C/svg%3E')] bg-no-repeat bg-[position:right_10px_center] bg-[length:16px] dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                                disabled={loading || !file.type.includes('pdf')}
                            >
                                <option value="fixed">Fixed Pages Per File</option>
                                <option value="custom">Custom Ranges</option>
                            </select>
                        </div>

                        {options.pageRangeMode === 'fixed' && (
                            <div className="mb-6 last:mb-0">
                                <label className="flex items-center font-semibold text-slate-800 mb-1 dark:text-slate-200">Pages Per File</label>
                                <p className="text-sm text-slate-500 mt-0 mb-2 dark:text-slate-400">
                                    Specify how many pages should be in each output file
                                </p>
                                <div className="flex items-center relative h-10">
                                    <input
                                        type="number"
                                        min="1"
                                        max="100"
                                        value={options.pagesPerFile || 1}
                                        onChange={(e) => handleOptionChange('pagesPerFile', parseInt(e.target.value) || 1)}
                                        className="w-full h-10 p-2.5 pr-[60px] bg-white border border-slate-300 rounded-md text-sm box-border outline-none focus:border-primary dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                                        disabled={loading}
                                    />
                                    <div className="absolute right-[1px] top-[1px] bottom-[1px] flex flex-row border-l border-slate-300 box-border overflow-hidden dark:border-slate-700 w-[60px]">
                                        <button
                                            className="w-1/2 border-none bg-slate-100 cursor-pointer flex items-center justify-center text-base font-bold select-none m-0 p-0 flex-1 leading-none rounded-tr-[3px] border-r border-slate-300 hover:not(:disabled):bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:hover:not(:disabled):bg-slate-700"
                                            onClick={() => handleOptionChange('pagesPerFile', Math.min(100, (options.pagesPerFile || 1) + 1))}
                                            disabled={loading || (options.pagesPerFile || 1) >= 100}
                                        >+</button>
                                        <button
                                            className="w-1/2 border-none bg-slate-100 cursor-pointer flex items-center justify-center text-base font-bold select-none m-0 p-0 flex-1 leading-none rounded-br-[3px] text-inherit hover:not(:disabled):bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-800 dark:text-white dark:hover:not(:disabled):bg-slate-700"
                                            onClick={() => handleOptionChange('pagesPerFile', Math.max(1, (options.pagesPerFile || 1) - 1))}
                                            disabled={loading || (options.pagesPerFile || 1) <= 1}
                                        >-</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {options.pageRangeMode === 'custom' && (
                            <div className="mb-6 last:mb-0">
                                <label className="flex items-center font-semibold text-slate-800 mb-1 dark:text-slate-200">Custom Page Ranges</label>
                                <p className="text-sm text-slate-500 mt-0 mb-2 dark:text-slate-400">
                                    Define specific page ranges for each output file
                                </p>

                                <div className="border border-slate-300 rounded-md p-3 bg-slate-50 dark:bg-slate-800/50 dark:border-slate-700">
                                    {customRanges.map((range, index) => (
                                        <div className="flex items-center mb-2.5" key={index}>
                                            <div className="flex items-center flex-1">
                                                <div className="flex-1">
                                                    <label className="block text-xs text-slate-500 mb-0.5 dark:text-slate-400">From</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={range.start}
                                                        onChange={(e) => updateRange(index, 'start', e.target.value)}
                                                        className="w-full p-2 border border-slate-300 rounded-md text-sm outline-none focus:border-primary dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                                                        disabled={loading}
                                                    />
                                                </div>
                                                <div className="mx-2 mt-4 text-slate-500 font-semibold dark:text-slate-400">-</div>
                                                <div className="flex-1">
                                                    <label className="block text-xs text-slate-500 mb-0.5 dark:text-slate-400">To</label>
                                                    <input
                                                        type="number"
                                                        min={range.start}
                                                        value={range.end}
                                                        onChange={(e) => updateRange(index, 'end', e.target.value)}
                                                        className="w-full p-2 border border-slate-300 rounded-md text-sm outline-none focus:border-primary dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                                                        disabled={loading}
                                                    />
                                                </div>
                                            </div>

                                            <button
                                                className="bg-transparent border-none text-red-500 cursor-pointer p-1.5 ml-2 flex items-center justify-center rounded-full hover:not(:disabled):bg-red-500/10 disabled:text-slate-300 disabled:cursor-not-allowed dark:disabled:text-slate-600"
                                                onClick={() => removeCustomRange(index)}
                                                disabled={loading || customRanges.length <= 1}
                                                type="button"
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ))}

                                    <button
                                        className="bg-primary text-white border-none py-2 px-3 rounded-md cursor-pointer flex items-center gap-1.5 text-sm w-fit transition-colors hover:not(:disabled):bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed"
                                        onClick={addCustomRange}
                                        disabled={loading}
                                        type="button"
                                    >
                                        <Plus size={16} /> Add Range
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {options.splitMethod === 'extraction' && (
                    <>
                        <div className="mb-6 last:mb-0">
                            <label className="flex items-center font-semibold text-slate-800 mb-1 relative dark:text-slate-200">
                                Pages to Extract
                                <span className="ml-1.5 cursor-help text-slate-500 inline-flex hover:text-primary" onMouseEnter={() => setShowTooltip('extraction')} onMouseLeave={() => setShowTooltip(null)}>
                                    <HelpCircle size={16} />
                                </span>
                            </label>
                            {showTooltip === 'extraction' && (
                                <div className="absolute bg-slate-800 text-white px-3 py-2 rounded text-[13px] z-[100] max-w-[300px] shadow-[0_2px_10px_rgba(0,0,0,0.2)] mt-1">
                                    <p className="m-1">Specify individual pages or page ranges to extract into separate files.</p>
                                </div>
                            )}
                            <p className="text-sm text-slate-500 mt-0 mb-2 dark:text-slate-400">
                                Define specific pages to extract from the document
                            </p>

                            <div className="border border-slate-300 rounded-md p-3 bg-slate-50 dark:bg-slate-800/50 dark:border-slate-700">
                                {customExtractions.map((range, index) => (
                                    <div className="flex items-center mb-2.5" key={index}>
                                        <div className="flex items-center flex-1">
                                            <div className="flex-1">
                                                <label className="block text-xs text-slate-500 mb-0.5 dark:text-slate-400">From</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={range.start}
                                                    onChange={(e) => updateExtraction(index, 'start', e.target.value)}
                                                    className="w-full p-2 border border-slate-300 rounded-md text-sm outline-none focus:border-primary dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                                                    disabled={loading}
                                                />
                                            </div>
                                            <div className="mx-2 mt-4 text-slate-500 font-semibold dark:text-slate-400">-</div>
                                            <div className="flex-1">
                                                <label className="block text-xs text-slate-500 mb-0.5 dark:text-slate-400">To</label>
                                                <input
                                                    type="number"
                                                    min={range.start}
                                                    value={range.end}
                                                    onChange={(e) => updateExtraction(index, 'end', e.target.value)}
                                                    className="w-full p-2 border border-slate-300 rounded-md text-sm outline-none focus:border-primary dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>

                                        <button
                                            className="bg-transparent border-none text-red-500 cursor-pointer p-1.5 ml-2 flex items-center justify-center rounded-full hover:not(:disabled):bg-red-500/10 disabled:text-slate-300 disabled:cursor-not-allowed dark:disabled:text-slate-600"
                                            onClick={() => removeCustomExtraction(index)}
                                            disabled={loading || customExtractions.length <= 1}
                                            type="button"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ))}

                                <button
                                    className="bg-primary text-white border-none py-2 px-3 rounded-md cursor-pointer flex items-center gap-1.5 text-sm w-fit transition-colors hover:not(:disabled):bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed"
                                    onClick={addCustomExtraction}
                                    disabled={loading}
                                    type="button"
                                >
                                    <Plus size={16} /> Add Extraction
                                </button>
                            </div>
                        </div>

                        <div className="mb-6 last:mb-0">
                            <label className="flex items-center font-semibold text-slate-800 mb-1 relative dark:text-slate-200">
                                Remaining Pages
                                <span className="ml-1.5 cursor-help text-slate-500 inline-flex hover:text-primary" onMouseEnter={() => setShowTooltip('remaining')} onMouseLeave={() => setShowTooltip(null)}>
                                    <HelpCircle size={16} />
                                </span>
                            </label>
                            {showTooltip === 'remaining' && (
                                <div className="absolute bg-slate-800 text-white px-3 py-2 rounded text-[13px] z-[100] max-w-[300px] shadow-[0_2px_10px_rgba(0,0,0,0.2)] mt-1">
                                    <p className="m-1 m-t-0"><strong>Ignore:</strong> Discard remaining pages</p>
                                    <p className="m-1"><strong>Bundle:</strong> Create a single file with all remaining pages</p>
                                    <p className="m-1 m-b-0"><strong>Separate:</strong> Create individual files for each remaining page</p>
                                </div>
                            )}
                            <p className="text-sm text-slate-500 mt-0 mb-2 dark:text-slate-400">
                                What to do with pages not included in extraction
                            </p>
                            <select
                                value={options.remainingAction || 'ignore'}
                                onChange={(e) => handleOptionChange('remainingAction', e.target.value)}
                                className="w-full p-2.5 bg-white border border-slate-300 rounded-md text-sm outline-none focus:border-primary appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg_xmlns=%27http://www.w3.org/2000/svg%27_fill=%27none%27_viewBox=%270_0_24_24%27_stroke=%27%2394a3b8%27%3E%3Cpath_stroke-linecap=%27round%27_stroke-linejoin=%27round%27_stroke-width=%272%27_d=%27M19_9l-7_7-7-7%27/%3E%3C/svg%3E')] bg-no-repeat bg-[position:right_10px_center] bg-[length:16px] dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                                disabled={loading}
                            >
                                <option value="ignore">Ignore</option>
                                <option value="bundle">Bundle into one file</option>
                                <option value="separate">Create separate files</option>
                            </select>
                        </div>
                    </>
                )}

                {options.splitMethod === 'size' && (
                    <div className="mb-6 last:mb-0">
                        <label className="flex items-center font-semibold text-slate-800 mb-1 dark:text-slate-200">Maximum Size Per Part</label>
                        <p className="text-sm text-slate-500 mt-0 mb-2 dark:text-slate-400">
                            Split file into parts no larger than this size
                        </p>
                        <div className="flex items-center gap-2.5">
                            <div className="flex items-center relative h-10 flex-1">
                                <input
                                    type="number"
                                    min={options.sizeUnit === 'MB' ? 0.1 : 100}
                                    max={options.sizeUnit === 'MB' ? 10 : 10000}
                                    step={options.sizeUnit === 'MB' ? 0.1 : 100}
                                    value={options.sizeUnit === 'MB'
                                        ? (options.maxSizeKB ? (options.maxSizeKB / 1024).toFixed(1) : '1.0')
                                        : options.maxSizeKB || 1024}
                                    onChange={(e) => {
                                        const value = parseFloat(e.target.value);
                                        if (isNaN(value)) return;

                                        const sizeInKB = options.sizeUnit === 'MB'
                                            ? Math.round(value * 1024)
                                            : value;

                                        handleOptionChange('maxSizeKB', sizeInKB);
                                    }}
                                    className="w-full h-10 p-2.5 pr-[60px] bg-white border border-slate-300 rounded-md text-sm box-border outline-none focus:border-primary dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                                    disabled={loading}
                                />
                                <div className="absolute right-[1px] top-[1px] bottom-[1px] flex flex-row border-l border-slate-300 box-border overflow-hidden dark:border-slate-700 w-[60px]">
                                    <button
                                        className="w-1/2 border-none bg-slate-100 cursor-pointer flex items-center justify-center text-base font-bold select-none m-0 p-0 flex-1 leading-none rounded-tr-[3px] border-r border-slate-300 hover:not(:disabled):bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:hover:not(:disabled):bg-slate-700"
                                        onClick={() => {
                                            const increment = options.sizeUnit === 'MB' ? 0.1 : 100;
                                            const max = options.sizeUnit === 'MB' ? 10 : 10000;
                                            const current = options.sizeUnit === 'MB'
                                                ? (options.maxSizeKB ? (options.maxSizeKB / 1024) : 1)
                                                : options.maxSizeKB || 1024;

                                            const newValue = Math.min(max, current + increment);
                                            const sizeInKB = options.sizeUnit === 'MB'
                                                ? Math.round(newValue * 1024)
                                                : newValue;

                                            handleOptionChange('maxSizeKB', sizeInKB);
                                        }}
                                        disabled={loading || (options.sizeUnit === 'MB'
                                            ? (options.maxSizeKB ? (options.maxSizeKB / 1024) : 1) >= 10
                                            : (options.maxSizeKB || 1024) >= 10000)}
                                    >+</button>
                                    <button
                                        className="w-1/2 border-none bg-slate-100 cursor-pointer flex items-center justify-center text-base font-bold select-none m-0 p-0 flex-1 leading-none rounded-br-[3px] text-inherit hover:not(:disabled):bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-800 dark:text-white dark:hover:not(:disabled):bg-slate-700"
                                        onClick={() => {
                                            const decrement = options.sizeUnit === 'MB' ? 0.1 : 100;
                                            const min = options.sizeUnit === 'MB' ? 0.1 : 100;
                                            const current = options.sizeUnit === 'MB'
                                                ? (options.maxSizeKB ? (options.maxSizeKB / 1024) : 1)
                                                : options.maxSizeKB || 1024;

                                            const newValue = Math.max(min, current - decrement);
                                            const sizeInKB = options.sizeUnit === 'MB'
                                                ? Math.round(newValue * 1024)
                                                : newValue;

                                            handleOptionChange('maxSizeKB', sizeInKB);
                                        }}
                                        disabled={loading || (options.sizeUnit === 'MB'
                                            ? (options.maxSizeKB ? (options.maxSizeKB / 1024) : 1) <= 0.1
                                            : (options.maxSizeKB || 1024) <= 100)}
                                    >
                                        -
                                    </button>
                                </div>
                            </div>
                            <div className="flex border border-slate-300 rounded-md overflow-hidden h-10 dark:border-slate-700 shrink-0">
                                <button
                                    className={`px-3 py-2 bg-slate-50 border-none cursor-pointer min-w-[40px] text-sm transition-colors hover:not(:disabled):not(.bg-primary):bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed dark:text-slate-200 ${options.sizeUnit === 'KB' ? 'bg-primary text-white hover:not(:disabled):bg-primary dark:bg-primary dark:text-white' : 'dark:bg-slate-800 dark:hover:not(:disabled):bg-slate-700'}`}
                                    onClick={() => handleOptionChange('sizeUnit', 'KB')}
                                    disabled={loading}
                                >
                                    KB
                                </button>
                                <button
                                    className={`px-3 py-2 bg-slate-50 border-none cursor-pointer min-w-[40px] text-sm transition-colors hover:not(:disabled):not(.bg-primary):bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed dark:text-slate-200 ${options.sizeUnit === 'MB' ? 'bg-primary text-white hover:not(:disabled):bg-primary dark:bg-primary dark:text-white' : 'dark:bg-slate-800 dark:hover:not(:disabled):bg-slate-700'}`}
                                    onClick={() => handleOptionChange('sizeUnit', 'MB')}
                                    disabled={loading}
                                >
                                    MB
                                </button>
                            </div>
                        </div>

                        {file.size && options.maxSizeKB && (
                            <div className="mt-2 text-[13px] text-slate-500 italic dark:text-slate-400">
                                Estimated parts: ~{Math.ceil(file.size / (options.maxSizeKB * 1024))}
                            </div>
                        )}
                    </div>
                )}

                {options.splitMethod === 'chunks' && (
                    <div className="mb-6 last:mb-0">
                        <label className="flex items-center font-semibold text-slate-800 mb-1 dark:text-slate-200">Number of Parts</label>
                        <p className="text-sm text-slate-500 mt-0 mb-2 dark:text-slate-400">
                            Split file into equal parts
                        </p>
                        <div className="flex items-center relative h-10 w-full sm:w-1/2">
                            <input
                                type="number"
                                min="2"
                                max="20"
                                value={options.chunks || 2}
                                onChange={(e) => handleOptionChange('chunks', parseInt(e.target.value) || 2)}
                                className="w-full h-10 p-2.5 pr-[60px] bg-white border border-slate-300 rounded-md text-sm box-border outline-none focus:border-primary dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                                disabled={loading}
                            />
                            <div className="absolute right-[1px] top-[1px] bottom-[1px] flex flex-row border-l border-slate-300 box-border overflow-hidden dark:border-slate-700 w-[60px]">
                                <button
                                    className="w-1/2 border-none bg-slate-100 cursor-pointer flex items-center justify-center text-base font-bold select-none m-0 p-0 flex-1 leading-none rounded-tr-[3px] border-r border-slate-300 hover:not(:disabled):bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:hover:not(:disabled):bg-slate-700"
                                    onClick={() => handleOptionChange('chunks', Math.min(20, (options.chunks || 2) + 1))}
                                    disabled={loading || (options.chunks || 2) >= 20}
                                >+</button>
                                <button
                                    className="w-1/2 border-none bg-slate-100 cursor-pointer flex items-center justify-center text-base font-bold select-none m-0 p-0 flex-1 leading-none rounded-br-[3px] text-inherit hover:not(:disabled):bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-800 dark:text-white dark:hover:not(:disabled):bg-slate-700"
                                    onClick={() => handleOptionChange('chunks', Math.max(2, (options.chunks || 2) - 1))}
                                    disabled={loading || (options.chunks || 2) <= 2}
                                >
                                    -
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="flex justify-between mt-6 max-md:flex-col max-md:px-4 max-md:pb-4 gap-4 pb-6 px-6">
                <button
                    className="py-2.5 px-5 bg-slate-100 text-slate-800 border border-slate-300 rounded-md font-semibold cursor-pointer text-sm transition-colors hover:not(:disabled):bg-slate-200 disabled:opacity-70 disabled:cursor-not-allowed dark:bg-transparent dark:text-slate-400 dark:border-slate-700 dark:hover:not(:disabled):bg-slate-800 dark:hover:not(:disabled):text-slate-200 max-md:w-full"
                    onClick={onBack}
                    disabled={loading}
                    type="button"
                >
                    Back
                </button>

                <button
                    className="py-2.5 px-5 bg-primary text-white border-none rounded-md font-semibold cursor-pointer text-sm transition-colors flex items-center justify-center hover:not(:disabled):bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed max-md:w-full"
                    onClick={onProcess}
                    disabled={loading}
                    type="button"
                >
                    {loading ? (
                        <>
                            <span className="inline-block w-4 h-4 border-2 border-white/30 rounded-full border-t-white animate-spin mr-2"></span>
                            Splitting...
                        </>
                    ) : (
                        <>
                            Split File
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default SplitOptions;
